import React, { useState, useRef } from "react";
import { LoadingOverlay } from "../components/LoadingOverlay";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

type Step = "email" | "no-method" | "totp" | "passkey" | "superadmin";

const GOOGLE_ERRORS: Record<string, string> = {
  no_account:        "No account found for this Google address. Ask your admin for an invite.",
  account_inactive:  "Your account has been deactivated. Contact your admin.",
  google_cancelled:  "Google sign-in was cancelled.",
  google_failed:     "Google sign-in failed. Please try again.",
  google_no_email:   "Could not retrieve your email from Google.",
  invalid_state:     "Invalid sign-in state. Please try again.",
};

function bufToBase64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = "";
  bytes.forEach(b => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function base64urlToUint8(s: string): Uint8Array {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

export default function LoginPage({ error: initialError }: { error?: string }) {
  const [step, setStep]         = useState<Step>("email");
  const [email, setEmail]       = useState("");
  const [digits, setDigits]     = useState(["", "", "", "", "", ""]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("error");
    if (e) window.history.replaceState({}, "", window.location.pathname);
    return (e && GOOGLE_ERRORS[e]) || initialError || "";
  });
  const [hasPasskey, setHasPasskey] = useState(false);
  const [hasTOTP, setHasTOTP]       = useState(false);
  const [saEmail, setSaEmail]       = useState("");
  const [saSecret, setSaSecret]     = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // ── Step 1: check which method to use ──────────────────────────────────────

  async function handleContinue() {
    if (!email || !email.includes("@")) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/check-method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Error"); return; }

      setHasTOTP(d.has_totp);
      setHasPasskey(d.has_passkey);

      if (d.method === "totp" || d.method === "both") {
        setStep("totp");
        setDigits(["", "", "", "", "", ""]);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else if (d.method === "passkey") {
        setStep("passkey");
      } else {
        setStep("no-method");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── TOTP flow ───────────────────────────────────────────────────────────────

  async function verifyTotp() {
    const code = digits.join("");
    if (code.length !== 6) { setError("Enter the 6-digit code"); return; }
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/totp/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.detail || "Invalid code"); return; }
      window.location.href = "/";
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── Super admin secret login ────────────────────────────────────────────────

  async function superAdminLogin() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/superadmin/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: saEmail, secret: saSecret }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.detail || "Invalid credentials"); return; }
      window.location.href = "/";
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── Passkey flow ────────────────────────────────────────────────────────────

  async function loginWithPasskey() {
    setLoading(true);
    setError("");
    try {
      const beginR = await fetch(`${API_URL}/api/auth/passkeys/begin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!beginR.ok) { const d = await beginR.json(); setError(d.detail || "Failed to begin passkey login"); return; }
      const opts = await beginR.json();

      // Convert base64url fields to ArrayBuffers for the WebAuthn API
      const publicKey: PublicKeyCredentialRequestOptions = {
        ...opts,
        challenge: base64urlToUint8(opts.challenge),
        allowCredentials: (opts.allowCredentials || []).map((c: any) => ({
          ...c,
          id: base64urlToUint8(c.id),
        })),
      };

      const credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential;
      if (!credential) { setError("Passkey authentication cancelled"); return; }

      const resp = credential.response as AuthenticatorAssertionResponse;
      const credPayload = {
        id: credential.id,
        rawId: bufToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON:    bufToBase64url(resp.clientDataJSON),
          authenticatorData: bufToBase64url(resp.authenticatorData),
          signature:         bufToBase64url(resp.signature),
          userHandle:        resp.userHandle ? bufToBase64url(resp.userHandle) : null,
        },
      };

      const finishR = await fetch(`${API_URL}/api/auth/passkeys/finish`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, credential: credPayload }),
      });
      if (!finishR.ok) { const d = await finishR.json(); setError(d.detail || "Passkey verification failed"); return; }
      window.location.href = "/";
    } catch (e: any) {
      if (e?.name === "NotAllowedError") {
        setError("Passkey authentication was cancelled or timed out.");
      } else {
        setError("Passkey error: " + (e?.message || String(e)));
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Google Sign-in ──────────────────────────────────��───────────────────────

  async function loginWithGoogle() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/google/url`);
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Google sign-in unavailable"); return; }
      window.location.href = d.auth_url;
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // ── Digit input helpers ─────────────────────────────────────────────────────

  function handleDigitInput(i: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...digits];
    next[i] = value.slice(-1);
    setDigits(next);
    if (value && i < 5) inputRefs.current[i + 1]?.focus();
    if (next.every(d => d !== "") && next.join("").length === 6) {
      setTimeout(() => verifyTotp(), 0);
    }
  }

  function handleDigitKeyDown(i: number, e: React.KeyboardEvent) {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus();
  }

  function handleDigitPaste(e: React.ClipboardEvent) {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) {
      setDigits(text.split(""));
      setTimeout(() => verifyTotp(), 0);
    }
  }

  // ── Styles ──────────────────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    background: "white", borderRadius: 16, padding: "48px 40px",
    width: "min(400px, calc(100vw - 32px))",
    boxShadow: "0 25px 50px rgba(0,0,0,0.3)", textAlign: "center",
  };

  const btnStyle = (disabled?: boolean): React.CSSProperties => ({
    width: "100%", padding: "13px 0", borderRadius: 10, border: "none",
    background: disabled ? "#94a3b8" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
    color: "white", fontSize: 15, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    marginTop: 8,
  });

  const ghostBtn: React.CSSProperties = {
    background: "none", border: `1.5px solid #e2e8f0`, borderRadius: 10,
    width: "100%", padding: "12px 0", fontSize: 14, fontWeight: 600,
    color: NAVY, cursor: "pointer", marginTop: 10,
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "12px 14px", borderRadius: 10, fontSize: 15,
    border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", color: "#1e293b",
  };

  const digitBoxes = (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 20 }}
         onPaste={handleDigitPaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => { inputRefs.current[i] = el; }}
          type="text" inputMode="numeric" maxLength={1} value={d}
          onChange={e => handleDigitInput(i, e.target.value)}
          onKeyDown={e => handleDigitKeyDown(i, e)}
          style={{
            width: 48, height: 56, textAlign: "center", fontSize: 24, fontWeight: 700,
            border: `2px solid ${d ? NAVY : "#e2e8f0"}`, borderRadius: 10,
            outline: "none", color: NAVY,
          }}
        />
      ))}
    </div>
  );

  const backBtn = (label = "← Use a different email") => (
    <button
      onClick={() => { setStep("email"); setDigits(["","","","","",""]); setError(""); }}
      style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 12,
               cursor: "pointer", marginTop: 12, textDecoration: "underline" }}
    >
      {label}
    </button>
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(135deg, ${NAVY} 0%, #0f172a 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <LoadingOverlay active={loading} />
      <div style={cardStyle}>
        <img src="/favicon.png" alt="HRxAI" style={{
          width: 64, height: 64, borderRadius: 14, objectFit: "contain",
          margin: "0 auto 20px", display: "block",
        }} />

        <h1 style={{ color: NAVY, fontSize: 24, fontWeight: 800, margin: "0 0 4px" }}>
          HRx<span style={{ color: TEAL }}>AI</span>
        </h1>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
            padding: "10px 14px", margin: "16px 0 0", fontSize: 13, color: "#dc2626",
          }}>
            {error}
          </div>
        )}

        {step === "email" && (
          <>
            <p style={{ color: "#64748b", fontSize: 13, margin: "8px 0 28px" }}>
              Sign in to your workspace
            </p>
            <input
              style={inputStyle} type="email" placeholder="you@company.com"
              value={email} autoFocus
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && email && handleContinue()}
            />
            <button style={btnStyle(!email || loading)} disabled={!email || loading} onClick={handleContinue}>
              {loading ? "Checking…" : "Continue →"}
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 4px" }}>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              <span style={{ fontSize: 12, color: "#94a3b8" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
            </div>

            <button
              onClick={loginWithGoogle}
              disabled={loading}
              style={{
                width: "100%", padding: "12px 0", borderRadius: 10,
                border: "1.5px solid #e2e8f0", background: "white",
                fontSize: 14, fontWeight: 600, color: "#334155",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>

            <button
              onClick={() => { setStep("superadmin"); setError(""); }}
              style={{ background: "none", border: "none", color: "#cbd5e1", fontSize: 11,
                       cursor: "pointer", marginTop: 20, textDecoration: "none" }}
            >
              Platform admin →
            </button>
          </>
        )}

        {step === "superadmin" && (
          <>
            <p style={{ color: "#64748b", fontSize: 13, margin: "8px 0 24px" }}>
              Platform administrator login
            </p>
            <input
              style={{ ...inputStyle, marginBottom: 10 }}
              type="email" placeholder="Super admin email"
              value={saEmail} autoFocus
              onChange={e => setSaEmail(e.target.value)}
            />
            <input
              style={{ ...inputStyle, marginBottom: 4 }}
              type="password" placeholder="Platform secret"
              value={saSecret}
              onChange={e => setSaSecret(e.target.value)}
              onKeyDown={e => e.key === "Enter" && saEmail && saSecret && superAdminLogin()}
            />
            <button
              style={btnStyle(!saEmail || !saSecret || loading)}
              disabled={!saEmail || !saSecret || loading}
              onClick={superAdminLogin}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <button
              onClick={() => { setStep("email"); setError(""); setSaEmail(""); setSaSecret(""); }}
              style={{ background: "none", border: "none", color: "#94a3b8", fontSize: 12,
                       cursor: "pointer", marginTop: 12, textDecoration: "underline" }}
            >
              ← Back
            </button>
          </>
        )}

        {step === "no-method" && (
          <>
            <p style={{ color: "#334155", fontWeight: 600, fontSize: 15, margin: "16px 0 8px" }}>
              No authenticator set up
            </p>
            <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 20px" }}>
              Try signing in with Google, or ask your admin to generate a{" "}
              <strong>Setup Link</strong> for your account.
            </p>
            <button
              onClick={loginWithGoogle}
              disabled={loading}
              style={{
                width: "100%", padding: "12px 0", borderRadius: 10,
                border: "1.5px solid #e2e8f0", background: "white",
                fontSize: 14, fontWeight: 600, color: "#334155",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>
            {backBtn("← Try a different email")}
          </>
        )}

        {step === "totp" && (
          <>
            <p style={{ color: "#64748b", fontSize: 13, margin: "8px 0 4px" }}>
              Enter your 6-digit Authenticator code
            </p>
            <p style={{ color: NAVY, fontWeight: 700, fontSize: 14, margin: "0 0 28px" }}>{email}</p>
            {digitBoxes}
            <button style={btnStyle(digits.join("").length !== 6 || loading)}
                    disabled={digits.join("").length !== 6 || loading} onClick={verifyTotp}>
              {loading ? "Verifying…" : "Sign in"}
            </button>
            {hasPasskey && (
              <button style={ghostBtn} onClick={() => { setStep("passkey"); setError(""); }}>
                Use passkey instead
              </button>
            )}
            {backBtn()}
          </>
        )}

        {step === "passkey" && (
          <>
            <p style={{ color: "#64748b", fontSize: 13, margin: "8px 0 28px" }}>
              Use your passkey to sign in as
              <br /><strong style={{ color: NAVY }}>{email}</strong>
            </p>
            <button style={btnStyle(loading)} disabled={loading} onClick={loginWithPasskey}>
              {loading ? "Waiting for passkey…" : "🔑 Sign in with Passkey"}
            </button>
            {hasTOTP && (
              <button style={ghostBtn} onClick={() => { setStep("totp"); setDigits(["","","","","",""]); setError(""); setTimeout(() => inputRefs.current[0]?.focus(), 100); }}>
                Use Authenticator app instead
              </button>
            )}
            {backBtn()}
          </>
        )}
      </div>
    </div>
  );
}
